import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActionPanel = () => {
  const quickActions = [
    {
      title: 'Process RFQs',
      description: 'Review and respond to pending quote requests',
      icon: 'FileText',
      color: 'bg-primary',
      link: '/rfq-processing',
      count: 12
    },
    {
      title: 'Customer Management',
      description: 'Manage customer accounts and communications',
      icon: 'Users',
      color: 'bg-success',
      link: '/customer-management',
      count: 5
    },
    {
      title: 'Generate Reports',
      description: 'Create business intelligence reports',
      icon: 'BarChart3',
      color: 'bg-warning',
      link: '/reports',
      count: null
    },
    {
      title: 'System Settings',
      description: 'Configure application and user permissions',
      icon: 'Settings',
      color: 'bg-secondary',
      link: '/admin-settings',
      count: null
    },
    {
      title: 'Product Catalog',
      description: 'Manage products and services catalog',
      icon: 'Package',
      color: 'bg-accent',
      link: '/product-catalog',
      count: null
    },
    {
      title: 'Payment Reconciliation',
      description: 'Review and reconcile payment transactions',
      icon: 'CreditCard',
      color: 'bg-primary',
      link: '/payment-reconciliation',
      count: 3
    }
  ];

  const recentActions = [
    {
      action: 'RFQ #12345 assigned to Sarah Johnson',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      user: 'Admin',
      type: 'assignment'
    },
    {
      action: 'Generated monthly sales report',
      timestamp: new Date(Date.now() - 45 * 60 * 1000),
      user: 'Admin',
      type: 'report'
    },
    {
      action: 'Updated product pricing for Network Solutions',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      user: 'Admin',
      type: 'update'
    },
    {
      action: 'Approved customer registration for TechCorp Ltd',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      user: 'Admin',
      type: 'approval'
    }
  ];

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'assignment': return 'UserCheck';
      case 'report': return 'FileText';
      case 'update': return 'Edit';
      case 'approval': return 'CheckCircle';
      default: return 'Activity';
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions Grid */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Quick Actions</h2>
          <Button variant="ghost" size="sm">
            <Icon name="MoreHorizontal" size={16} />
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions?.map((action, index) => (
            <Link
              key={index}
              to={action?.link}
              className="relative group p-4 bg-background border border-border rounded-lg hover:shadow-moderate transition-smooth"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 ${action?.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <Icon name={action?.icon} size={20} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-smooth">
                    {action?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {action?.description}
                  </p>
                </div>
                {action?.count && (
                  <span className="absolute top-2 right-2 w-6 h-6 bg-error text-error-foreground text-xs font-medium rounded-full flex items-center justify-center">
                    {action?.count}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
      {/* Recent Actions */}
      <div className="bg-card border border-border rounded-lg shadow-subtle">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Recent Administrative Actions</h2>
            <Button variant="ghost" size="sm">
              <Icon name="History" size={16} />
              View All
            </Button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {recentActions?.map((item, index) => (
            <div key={index} className="p-4 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Icon name={getActionIcon(item?.type)} size={14} className="text-muted-foreground" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{item?.action}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-muted-foreground">by {item?.user}</span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground">{formatTimeAgo(item?.timestamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickActionPanel;